import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Cable, RefreshCw, Save, Loader2, AlertTriangle, Check,
  BedDouble, Tag, Info, Link2
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

// Normalise un libellé en code de convention : MAJUSCULES, sans accents,
// séparateurs -> underscore. Autorise le tiret (format CHAMBRE_DOUBLE-BAR).
const toCode = (s) =>
  (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

// ── Petit toggle façon Komorotel ──
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative w-9 h-5 rounded-full transition shrink-0 ${
        checked ? "bg-[var(--k-green)]" : "bg-gray-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  );
}

// ── Champ input réutilisable ──
function Field({ label, value, onChange, placeholder, mono }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none
          focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition
          ${mono ? "font-mono text-xs" : ""}`}
      />
    </div>
  );
}

export default function SuperAdminConnectivity() {
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Catalogue de l'hôtel sélectionné
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);

  // Formulaire connexion
  const [form, setForm] = useState({
    label: "", hciHotelId: "", hotelCode: "", partnerId: "komorotel",
    active: true, managedByCM: false,
  });
  // Mappings : dict par id pour édition facile
  const [roomMap, setRoomMap] = useState({});       // roomId -> { hciRoomTypeId, roomCode }
  const [rateMap, setRateMap] = useState({});       // `${planId}_${roomId}` -> { hciRatePlanId, rateplanCode }

  // ── Charger la liste des hôtels une fois ──
  useEffect(() => {
    axios.get(`${API}/hotels`, { withCredentials: true })
      .then(r => setHotels(r.data))
      .catch(console.error);
  }, []);

  // ── Charger la connexion + catalogue quand on choisit un hôtel ──
  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    setSaved(false);
    axios.get(`${API}/cm-connection/${hotelId}`, { withCredentials: true })
      .then(({ data }) => {
        setRooms(data.rooms || []);
        setRatePlans(data.ratePlans || []);

        const c = data.connection;
        setForm({
          label: c?.label || "",
          hciHotelId: c?.hciHotelId || "",
          hotelCode: c?.hotelCode || "",
          partnerId: c?.partnerId || "komorotel",
          active: c?.active !== false,
          managedByCM: data.hotel?.managedByCM || false,
        });

        // Prefill room mappings
        const rm = {};
        (c?.roomMappings || []).forEach(m => {
          rm[String(m.komorotelRoomId)] = {
            hciRoomTypeId: m.hciRoomTypeId || "",
            roomCode: m.roomCode || "",
          };
        });
        setRoomMap(rm);

        // Prefill rate mappings (par couple plan+chambre)
        const pm = {};
        (c?.ratePlanMappings || []).forEach(m => {
          pm[`${m.komorotelRatePlanId}_${m.komorotelRoomId}`] = {
            hciRatePlanId: m.hciRatePlanId || "",
            rateplanCode: m.rateplanCode || "",
          };
        });
        setRateMap(pm);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [hotelId]);

  // ── Construit la liste des couples (tarif × chambre liée) ──
  const couples = useMemo(() => {
    const roomsById = Object.fromEntries(rooms.map(r => [String(r._id), r]));
    const out = [];
    for (const plan of ratePlans) {
      for (const rid of (plan.roomIds || [])) {
        const room = roomsById[String(rid)];
        if (!room) continue; // chambre inactive/supprimée -> on ignore
        out.push({ plan, room, key: `${plan._id}_${rid}` });
      }
    }
    return out;
  }, [ratePlans, rooms]);

  const setRoomField = (roomId, field, val) =>
    setRoomMap(prev => ({ ...prev, [roomId]: { ...prev[roomId], [field]: val } }));

  const setRateField = (key, field, val) =>
    setRateMap(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));

  // ── Sauvegarde ──
  const handleSave = async () => {
    if (!form.hciHotelId.trim() || !form.hotelCode.trim()) {
      alert("hciHotelId et hotelCode sont requis.");
      return;
    }
    setSaving(true);
    setSaved(false);

    const roomMappings = rooms.map(r => ({
      komorotelRoomId: r._id,
      hciRoomTypeId: (roomMap[String(r._id)]?.hciRoomTypeId || "").trim(),
      roomCode: (roomMap[String(r._id)]?.roomCode || "").trim(),
    })).filter(m => m.hciRoomTypeId || m.roomCode);

    const ratePlanMappings = couples.map(({ plan, room, key }) => ({
      komorotelRatePlanId: plan._id,
      komorotelRoomId: room._id,
      hciRatePlanId: (rateMap[key]?.hciRatePlanId || "").trim(),
      rateplanCode: (rateMap[key]?.rateplanCode || "").trim(),
    })).filter(m => m.hciRatePlanId || m.rateplanCode);

    try {
      await axios.put(`${API}/cm-connection/${hotelId}`, {
        ...form, roomMappings, ratePlanMappings,
      }, { withCredentials: true });
      setSaved(true);
    } catch (err) {
      alert(err.response?.data?.error || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center">
            <Cable className="text-white" size={16} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Connectivité PMS</h1>
            <p className="text-sm text-gray-400 mt-0.5">Relier un hôtel au PMS HCI Digit (mapping chambres & tarifs)</p>
          </div>
        </div>
      </div>

      {/* ── SÉLECTEUR D'HÔTEL ── */}
      <div className="bg-white border border-[#e5e7eb] rounded-2xl p-4 flex items-center gap-3">
        <label className="text-sm text-gray-500 shrink-0">Hôtel</label>
        <select
          value={hotelId}
          onChange={e => setHotelId(e.target.value)}
          className="flex-1 max-w-md px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-700"
        >
          <option value="">— Sélectionner un hôtel —</option>
          {hotels.map(h => (
            <option key={h._id} value={h._id}>{h.name}</option>
          ))}
        </select>
        {hotelId && (
          <button
            onClick={() => setHotelId(hotelId)}
            className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
            title="Recharger"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        )}
      </div>

      {!hotelId ? (
        <div className="bg-white border border-[#e5e7eb] rounded-2xl py-16 flex flex-col items-center gap-2">
          <Link2 className="text-gray-300" size={28} />
          <p className="text-xs text-gray-400">Choisis un hôtel pour configurer sa connexion HCI Digit.</p>
        </div>
      ) : loading ? (
        <div className="bg-white border border-[#e5e7eb] rounded-2xl py-16 text-center">
          <p className="text-xs text-gray-400">Chargement...</p>
        </div>
      ) : (
        <>
          {/* ── CARTE CONNEXION ── */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5 flex flex-col gap-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-widest">Connexion HCI Digit</p>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Libellé" value={form.label}
                onChange={v => setForm(f => ({ ...f, label: v }))}
                placeholder="Le Retail — HCI Digit" />
              <Field label="Partner ID" value={form.partnerId}
                onChange={v => setForm(f => ({ ...f, partnerId: v }))}
                placeholder="komorotel" mono />
              <Field label="HCI Hotel ID (interne)" value={form.hciHotelId}
                onChange={v => setForm(f => ({ ...f, hciHotelId: v }))}
                placeholder="cmjfupzoa00037k38vgpczdj9" mono />
              <Field label="Hotel Code" value={form.hotelCode}
                onChange={v => setForm(f => ({ ...f, hotelCode: v }))}
                placeholder="Identifiant hôtel HCI" mono />
            </div>

            <div className="flex items-center gap-6 pt-1">
              <div className="flex items-center gap-2">
                <Toggle checked={form.active} onChange={v => setForm(f => ({ ...f, active: v }))} />
                <span className="text-sm text-gray-600">Connexion active</span>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  checked={form.managedByCM}
                  onChange={v => setForm(f => ({ ...f, managedByCM: v }))}
                  disabled={!form.active}
                />
                <span className="text-sm text-gray-600">Géré par le PMS (back-office en lecture seule)</span>
              </div>
            </div>

            {form.managedByCM && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Prix et disponibilités de cet hôtel deviennent <b>non modifiables</b> dans le back-office :
                  ils seront pilotés par HCI Digit. Assure-toi que le mapping ci-dessous est complet avant d'activer.
                </p>
              </div>
            )}
          </div>

          {/* ── MAPPING CHAMBRES ── */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-[#e5e7eb]">
              <BedDouble size={15} className="text-gray-400" />
              <p className="text-sm font-medium text-gray-900">Mapping des chambres</p>
              <span className="text-[11px] text-gray-400">({rooms.length})</span>
            </div>

            {rooms.length === 0 ? (
              <p className="px-5 py-8 text-center text-xs text-gray-400">Aucune chambre active.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] text-left">
                      {["Chambre Komorotel", "HCI roomTypeId (envoi résa)", "roomCode (réception)"].map(h => (
                        <th key={h} className="px-5 py-2.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map(r => {
                      const id = String(r._id);
                      return (
                        <tr key={id} className="border-b border-gray-50 last:border-0">
                          <td className="px-5 py-2.5 text-gray-700 whitespace-nowrap">{r.title}</td>
                          <td className="px-5 py-2.5">
                            <input
                              value={roomMap[id]?.hciRoomTypeId || ""}
                              onChange={e => setRoomField(id, "hciRoomTypeId", e.target.value)}
                              placeholder="cmkr3s14i000jn001b66hphyo"
                              className="w-full px-2.5 py-1.5 text-xs font-mono border border-[#e5e7eb] rounded-lg outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                            />
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              value={roomMap[id]?.roomCode || ""}
                              onChange={e => setRoomField(id, "roomCode", toCode(e.target.value))}
                              placeholder="CHAMBRE_DOUBLE"
                              className="w-full px-2.5 py-1.5 text-xs font-mono border border-[#e5e7eb] rounded-lg outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── MAPPING TARIFS (couples plan × chambre) ── */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-[#e5e7eb]">
              <Tag size={15} className="text-gray-400" />
              <p className="text-sm font-medium text-gray-900">Mapping des tarifs</p>
              <span className="text-[11px] text-gray-400">({couples.length} couples)</span>
            </div>

            <div className="flex items-start gap-2 px-5 py-2.5 bg-[#f9fafb] border-b border-[#e5e7eb]">
              <Info size={13} className="text-gray-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-500">
                Un tarif lié à plusieurs chambres apparaît une fois par chambre : chaque couple a son propre
                <span className="font-mono"> rateplanCode</span> (ex. <span className="font-mono">CHAMBRE_DOUBLE-BAR</span>).
              </p>
            </div>

            {couples.length === 0 ? (
              <p className="px-5 py-8 text-center text-xs text-gray-400">Aucun tarif lié à une chambre.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] text-left">
                      {["Tarif × Chambre", "HCI ratePlanId (envoi résa)", "rateplanCode (réception)"].map(h => (
                        <th key={h} className="px-5 py-2.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {couples.map(({ plan, room, key }) => (
                      <tr key={key} className="border-b border-gray-50 last:border-0">
                        <td className="px-5 py-2.5 whitespace-nowrap">
                          <span className="text-gray-800">{plan.name}</span>
                          <span className="text-gray-400"> · {room.title}</span>
                        </td>
                        <td className="px-5 py-2.5">
                          <input
                            value={rateMap[key]?.hciRatePlanId || ""}
                            onChange={e => setRateField(key, "hciRatePlanId", e.target.value)}
                            placeholder="cmrktiuvt000tn401tkjt3u7c"
                            className="w-full px-2.5 py-1.5 text-xs font-mono border border-[#e5e7eb] rounded-lg outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                          />
                        </td>
                        <td className="px-5 py-2.5">
                          <input
                            value={rateMap[key]?.rateplanCode || ""}
                            onChange={e => setRateField(key, "rateplanCode", toCode(e.target.value))}
                            placeholder="CHAMBRE_DOUBLE-BAR"
                            className="w-full px-2.5 py-1.5 text-xs font-mono border border-[#e5e7eb] rounded-lg outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── BARRE D'ACTION ── */}
          <div className="sticky bottom-0 bg-[#f9fafb] pt-1 pb-2">
            <div className="flex items-center justify-end gap-3">
              {saved && (
                <span className="flex items-center gap-1.5 text-xs text-[var(--k-green)]">
                  <Check size={14} /> Enregistré
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-[var(--k-green)] text-white text-sm font-medium rounded-xl hover:bg-[var(--k-green-hover)] transition disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Enregistrer la connexion
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}